import type { Module } from 'vuex'
import dayjs from 'dayjs'
import type { CampaignState, CampaignTypeFilter } from './types'
import { campaignActions } from './actions'
import { initScratchCampaign } from './utils'
import type { RootState } from '@/store/modules/types'
import type { Campaign, CampaignRegistrationStats } from '@/types/resources/campaign'
import { clone } from '@/utils/helpers'

export const initialCampaignState = (): CampaignState => ({
  // Campaigns
  campaigns: [],
  totalCampaignsCount: 0,
  hasNoMoreCampaigns: false,
  isFetchingCampaigns: false,
  campaignsTypeFilter: 'all',

  // Current Campaign
  currentCampaign: null,
  isFetchingCampaign: false,
  currentCampaignRegistrationStats: null,
  isFetchingCampaignRegistrationStats: false,

  // Scratch Campaign
  scratchCampaign: initScratchCampaign(),
  isUpdatingCampaign: false,
  isCreatingCampaign: false,
  currentSubscriptionLevel: null,
})

const campaignModule: Module<CampaignState, RootState> = {
  namespaced: true,
  actions: campaignActions,
  state: initialCampaignState,
  mutations: {
    // Current Campaign
    RESET_CURRENT_CAMPAIGN(state) {
      state.currentCampaign = null
      state.currentCampaignRegistrationStats = null
    },
    SET_CURRENT_CAMPAIGN(state, campaign: Campaign) {
      state.currentCampaign = clone(campaign)
    },
    SET_IS_FETCHING_CAMPAIGN(state, isFetching: boolean) {
      state.isFetchingCampaign = isFetching
    },
    SET_CURRENT_CAMPAIGN_REGISTRATION_STATS(state, registrationStats: CampaignRegistrationStats) {
      state.currentCampaignRegistrationStats = registrationStats
    },
    SET_IS_FETCHING_CAMPAIGN_REGISTRATION_STATS(state, isFetching: boolean) {
      state.isFetchingCampaignRegistrationStats = isFetching
    },
    // Scratch Campaign
    RESET_SCRATCH_CAMPAIGN(state) {
      state.scratchCampaign = initScratchCampaign()
    },
    SET_SCRATCH_CAMPAIGN(state, campaign: Campaign) {
      state.scratchCampaign = clone(campaign)
    },
    SET_IS_UPDATING_CAMPAIGN(state, isUpdating: boolean) {
      state.isUpdatingCampaign = isUpdating
    },
    SET_IS_CREATING_CAMPAIGN(state, isCreating: boolean) {
      state.isCreatingCampaign = isCreating
    },
    // Campaigns
    SET_CAMPAIGNS(state, campaigns: Campaign[]) {
      state.campaigns = campaigns
    },
    CONCAT_CAMPAIGNS(state, campaigns: Campaign[]) {
      state.campaigns = clone(state.campaigns.concat(campaigns))
    },
    RESET_CAMPAIGNS(state) {
      state.campaigns = []
    },
    REMOVE_CAMPAIGN(state, campaignOid: number) {
      state.campaigns = state.campaigns.filter(campaign => campaign.oid !== campaignOid)
    },
    SET_IS_FETCHING_CAMPAIGNS(state, isFetching: boolean) {
      state.isFetchingCampaigns = isFetching
    },
    SET_TOTAL_CAMPAIGNS_COUNT(state, count: number) {
      state.totalCampaignsCount = count
    },
    SET_CAMPAIGNS_TYPE_FILTER(state, value: CampaignTypeFilter) {
      state.campaignsTypeFilter = value
    },
    // Campaigns
    SET_CURRENT_CAMPAIGN_SUBSCRIPTION_LEVEL(state, subscriptionLevel: string | null) {
      state.currentSubscriptionLevel = subscriptionLevel
    },
    RESET_CURRENT_CAMPAIGN_SUBSCRIPTION_LEVEL(state) {
      state.currentSubscriptionLevel = null
    },

    CAMPAIGN_FORM_IS_VALID(state, isValid: boolean) {
      state.scratchCampaign.isValid = isValid
    },

  },
  getters: {
    currentCampaignIsActive(state) {
      if (!state.currentCampaign) {
        return false
      }
      return dayjs(state.currentCampaign.endDate).isAfter(new Date())
    },
    currentCampaignFirstFbPageId(state): string | null {
      if (!state.currentCampaign) {
        return null
      }
      if (!state.currentCampaign.socialActions?.socialAccounts) {
        return null
      }
      const facebookAccount = state.currentCampaign.socialActions.socialAccounts.find((item) => {
        const keys = Object.keys(item)
        if (keys.indexOf('facebook:messenger') > -1) return item
      })
      if (!facebookAccount) {
        return null
      }

      const messengerAction = facebookAccount.actions.find(action => action.key === 'facebook:messenger')
      return messengerAction ? messengerAction.value : null
    },
    campaignsList(state) {
      return state.campaigns
    },
  },
}

export default campaignModule
